/** Static checklist copy extracted from res/GL/GL/PCL 25-26 31-08-26.xlsm — Section A */

import type {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';

export type A12RowKind = 'general' | 'main' | 'sub' | 'na' | 'note';

export interface A12Row {
    id: string;
    kind: A12RowKind;
    num?: string;
    text: string;
    /** 0=main, 1=lettered sub, 2=roman under letter — set by Admin / seed normalize */
    level?: 0 | 1 | 2;
}

export interface A13Row {
    id: string;
    num: string;
    text: string;
    level?: 0 | 1 | 2;
}

export const A_SECTION_PAGES = ['Ai', 'A1', 'A1.2', 'A1.3'] as const;
export type ASectionPageCode = typeof A_SECTION_PAGES[number];

/** Short titles from Excel Ai index (shown beside page codes in the left rail). */
export const A_PAGE_TITLES: Record<ASectionPageCode, string> = {
    Ai: 'Index',
    A1: 'Final completion memorandum',
    'A1.2': 'Final subsequent events review',
    'A1.3': 'Final completion checklist',
};

/** A-section TOC from PCL Ai sheet (lower half — A FINAL COMPLETION). */
export type ASectionIndexRow = {
    id: string;
    kind: 'item' | 'section';
    num: string;
    title: string;
    status?: string;
    /** Navigable workpaper when implemented. */
    pageCode?: Exclude<ASectionPageCode, 'Ai'>;
};

export const A_SECTION_INDEX_LETTER = 'A';
export const A_SECTION_INDEX_TITLE = 'FINAL COMPLETION';

export const A_SECTION_INDEX: ASectionIndexRow[] = [
    {id: 'a-1', kind: 'item', num: '1', title: 'Final completion memorandum', status: 'a', pageCode: 'A1'},
    {id: 'a-1.2', kind: 'item', num: '1.2', title: 'Final subsequent events review', status: 'a', pageCode: 'A1.2'},
    {id: 'a-1.3', kind: 'item', num: '1.3', title: 'Final completion checklist', status: 'a', pageCode: 'A1.3'},
    {id: 'a-2', kind: 'section', num: '2', title: 'Accounts & tax computations'},
    {id: 'a-2.1', kind: 'item', num: '2.1', title: 'Final accounts', status: 'a'},
    {id: 'a-2.2', kind: 'item', num: '2.2', title: 'Tax computations', status: 'a'},
    {id: 'a-3', kind: 'item', num: '3', title: 'Final journals', status: 'a'},
    {
        id: 'a-4',
        kind: 'item',
        num: '4',
        title: 'Draft accounts, typing instructions, calling over checklist  (Aapp1)',
        status: 'a',
    },
    {id: 'a-5', kind: 'item', num: '5', title: 'Letter of representation', status: 'a'},
    {id: 'a-6', kind: 'item', num: '6', title: 'Letter to management', status: '/'},
    {id: 'a-7', kind: 'item', num: '7', title: 'Company accounts disclosure checklists', status: '/'},
    {id: 'a-8', kind: 'item', num: '8', title: "Management accounts/last year's accounts", status: 'a'},
    {id: 'a-9', kind: 'item', num: '9', title: ''},
];

export const A1_CONTENT = {
    title: "FINAL COMPLETION MEMORANDUM",
    intro: "The purpose of this memorandum is to document any matters arising between the issue of the accounts to the client for approval and completion of the audit report. These can be either free form notes, a reference to the relevant checklist or a combination of both. See the guidance notes for further information on the completion of this form.",
    section1Title: "1. Approval for Signing the Audit Report",
    confirmIntro: "I confirm that:",
    confirmItems: [
        {num: "1", text: "The audit complies with professional standards and applicable legal and regulatory requirements."},
        {num: "2", text: "In particular the audit complies with all relevant requirements of the HKSAs."},
        {num: "3", text: "A sufficient and appropriate record for the basis of the audit report has been documented."},
        {num: "4", text: "There are no factors to indicate that the representations received from the directors cannot be relied upon."},
        {num: "5", text: "The auditor’s report issued is appropriate in the circumstances."}
    ],
    authorizeText: "I authorise the issue of the completion of the audit report.",
    partnerLabel: "Audit engagement partner",
    eqrLabel: "Engagement quality reviewer (EQR) / other partner (where appropriate)",
    section2Title: "2. Matters outstanding on B1",
    section3Title: "3. Final Subsequent Events Review",
    section3RefLabel: "A1.2 Completed - YES / NO",
    section3DefaultText: "A formal update is done and there are no outstanding areas of concern and no issues were expected to arise. We have also reviewed the cash book, invoices, bank statements and the minutes and no subsequent events were noted.",
    section4Title: "4. Other (Including late amendments to the file)",
    section4RefLabel: "A1.3 Completed - YES / NO",
    footerNote: "Note.  Where the auditor finds it necessary to modify existing audit documentation or add new audit documentation after the assembly of the final audit file has been completed (see test 17 on A1.3), the auditor shall, regardless of the nature of the modifications or additions, document:  \n(a) The specific reasons for making them; and \n(b) When and by whom they were made and reviewed (HKSA 230.16)"
} as const;

export const A12_ROWS: A12Row[] = [
    {id: "r6", kind: "general", text: "General"},
    {id: "r7", kind: "main", text: "Establish whether the management have effective procedures to ensure that subsequent events are identified.", num: "1"},
    {id: "r8", kind: "main", text: "Read the management minutes held since the final audit and enquire about matters discussed at meetings for which minutes are not yet available.", num: "2"},
    {id: "r9", kind: "main", text: "Review any available accounting records and identify whether anything needs to be reflected in the financial statements.", num: "3"},
    {id: "r10", kind: "main", text: "Enquire with management whether any subsequent events have occurred which might affect the accounts. Cover specifically:", num: "4"},
    {id: "r11", kind: "sub", text: "the current status of accounting estimates, other items involving subjective judgement or which were accounted for on the basis of preliminary data: specify:", num: "(a)"},
    {id: "r12", kind: "na", text: "N/A"},
    {id: "r14", kind: "sub", text: "whether new commitments, borrowings or guarantees have been entered into;", num: "(b)"},
    {id: "r15", kind: "sub", text: "whether sales of assets have occurred or are planned;", num: "(c)"},
    {id: "r16", kind: "sub", text: "whether the issue of new shares or debentures, or an agreement to merge or to liquidate, has been made or is planned;", num: "(d)"},
    {id: "r17", kind: "sub", text: "whether any assets have been destroyed;", num: "(e)"},
    {id: "r18", kind: "sub", text: "whether there have been any developments regarding risk areas and contingencies;", num: "(f)"},
    {id: "r19", kind: "sub", text: "whether any unusual accounting adjustments have been made or are contemplated;", num: "(g)"},
    {id: "r20", kind: "sub", text: "whether any events have occurred or are likely to occur which might bring into question the appropriateness of the accounting policies.", num: "(h)"},
    {id: "r21", kind: "sub", text: "whether events have occurred that are relevant to the measurement of estimates or provisions.", num: "(i)"},
    {id: "r22", kind: "sub", text: "whether any events have occurred that are relevant to the recovery of assets.", num: "(j)"},
    {id: "r23", kind: "main", text: "Consider, where appropriate, the validity of the going concern basis of accounting.", num: "5"},
    {id: "r24", kind: "main", text: "Confirm that the directors’ review of the future of the business still extends to a period of at least 12 months from the date of the financial statements.", num: "6"},
    {id: "r25", kind: "main", text: "In the case of a group audit, perform procedures, including, as appropriate, requesting component auditors to perform procedures, designed to identify events that may require adjustment of or disclosure in, the group financial statements.", num: "7"},
    {id: "r26", kind: "note", text: "Note: See paragraph A150 for further guidance when applying paragraph 49 of HKSA 600 (Revised)."},
    {id: "r28", kind: "note", text: "Note: See paragraphs 10 to 17 of HKSA 560 and the corresponding application materials for requirements on:\n- Facts which become known to the auditor after the date of the auditor’s report but before the date the financial statements are issued\n- Facts which become known to the auditor after the financial statements have been issued"},
];

export const A13_ROWS: A13Row[] = [
    {id: "r6", num: "1", text: "Have all outstanding items on the initial partner completion on B1 been adequately dealt with?"},
    {id: "r7", num: "2", text: "Has a signed letter of representation addressed to the firm been received from management that addresses at least the specific issues required by the HKSAs (see guidance notes)?"},
    {id: "r8", num: "3", text: "Is the letter of representation dated near as practicable to but not after the expected date of the audit report; and do the representations include all those that relate to all financial statements and all periods referred to in the report?"},
    {id: "r9", num: "4", text: "Where fraud was found or suspected was this and any other relevant information communicated to the appropriate level of management and those charged with governance on a timely basis?"},
    {id: "r10", num: "5", text: "Where the letter of representation is signed by one director on behalf of the Board, have we seen minutes of a meeting agreeing its contents?"},
    {id: "r11", num: "6", text: "Are we satisfied that the directors' representations can be relied upon?"},
    {id: "r12", num: "7", text: "Have any inconsistencies between the directors' representations and other evidence been resolved?"},
    {id: "r13", num: "8", text: "Does the file contain adequate justification of the audit report?"},
    {id: "r14", num: "9", text: "Have we informed the directors of any unadjusted misstatements, any material weaknesses in the accounting and internal control systems, or any other relevant matters relating to the audit?"},
    {id: "r15", num: "10", text: "Has the final Subsequent Events Review programme on A1.2 been completed?"},
    {id: "r16", num: "11", text: "Does the balance sheet state the name(s) of the director(s) who approves the financial statements on behalf of the Board, together with the date of approval?"},
    {id: "r17", num: "12", text: "Where applicable does the audit file contain sufficient evidence to support reporting under the SME-FRF & FRS (Revised) or HKFRS for Private Entities?"},
    {id: "r18", num: "13", text: "Do the working papers and the tax computation reflect final adjustments?"},
    {id: "r19", num: "14", text: "Have all final journals been recorded and processed to produce a closing trial balance agreeing with the financial statements?"},
    {id: "r20", num: "15", text: "Has the final copy of the financial statements been referenced to the file to ensure all lead and Q schedules reflect final adjustments?"},
    {id: "r21", num: "16", text: "On consideration of the file and in particular questions 22 and 23 on B4 are we satisfied that it is appropriate for us to seek re-appointment as auditors?"},
    {id: "r22", num: "17", text: "Will the assembly of the file be completed on a timely basis, ordinarily not more than 60 days, after the date of the audit report?"}
];

export const A13_FOOTER = {
    signedLabel: "Audit report signed on",
    deadlineLabel: "Deadline for assembly of file",
    assemblyCompletedLabel: "File assembly completed",
    footerNote: "Note.  Once the assembly of the final audit file has been completed, the auditor shall not delete or discard audit documentation of any nature before the end of its retention period (HKSA 230.15)"
} as const;

/**
 * Excel A1.2 sample defaults (Test required / Results / Comments).
 * Company-specific yellow cells (enquiry contact, dates) filled separately.
 */
export const A12_DEFAULT_VALUES: Record<string, string> = {
    // 1
    'r7.testRequired': 'Y',
    'r7.resultsSatisfactory': 'Y',
    // 2
    'r8.testRequired': 'Y',
    'r8.resultsSatisfactory': 'Y',
    'r8.comments': 'No matters',
    // 3
    'r9.testRequired': 'Y',
    'r9.resultsSatisfactory': 'Y',
    // 4 (a)
    'r11.testRequired': 'Y',
    'r11.resultsSatisfactory': 'Y',
    // 4 (b)–(j)
    'r14.testRequired': 'Y',
    'r14.resultsSatisfactory': 'Y',
    'r14.comments': 'N/A',
    'r15.testRequired': 'Y',
    'r15.resultsSatisfactory': 'Y',
    'r15.comments': 'N/A',
    'r16.testRequired': 'Y',
    'r16.resultsSatisfactory': 'Y',
    'r16.comments': 'N/A',
    'r17.testRequired': 'Y',
    'r17.resultsSatisfactory': 'Y',
    'r17.comments': 'N/A',
    'r18.testRequired': 'Y',
    'r18.resultsSatisfactory': 'Y',
    'r18.comments': 'N/A',
    'r19.testRequired': 'Y',
    'r19.resultsSatisfactory': 'Y',
    'r19.comments': 'N/A',
    'r20.testRequired': 'Y',
    'r20.resultsSatisfactory': 'Y',
    'r20.comments': 'N/A',
    'r21.testRequired': 'Y',
    'r21.resultsSatisfactory': 'Y',
    'r21.comments': 'N/A',
    'r22.testRequired': 'Y',
    'r22.resultsSatisfactory': 'Y',
    'r22.comments': 'N/A',
    // 5
    'r23.testRequired': 'Y',
    'r23.resultsSatisfactory': 'Y',
    'r23.comments': 'Yes',
    // 6
    'r24.testRequired': 'Y',
    'r24.resultsSatisfactory': 'Y',
    'r24.comments': 'Yes',
    // 7 group audit
    'r25.testRequired': 'N/A',
};

export function buildA12EnquiryComment(directorName?: string | null): string {
    const name = (directorName || '').trim();
    return name ? `Enquired with ${name}` : 'Enquired with';
}

/** A1.2 fields driven by A1 J15 (partnerDate): I1 completedDate, I2 reviewedDate, H11 r11.comments */
export const A12_DATE_FROM_A1_KEYS = ['completedDate', 'reviewedDate', 'r11.comments'] as const;

/** Rows that show Initials (General + numbered / lettered procedures). */
export function a12SignOffFieldKeys(rows: A12Row[] = A12_ROWS): string[] {
    const keys: string[] = ['generalInitials'];
    for (const row of rows) {
        if ((row.kind === 'main' || row.kind === 'sub') && row.num) {
            keys.push(`${row.id}.initialsDate`);
        }
    }
    return keys;
}

/** Per-row sign-off date cells (after Initials column). */
export function a12SignOffDateFieldKeys(rows: A12Row[] = A12_ROWS): string[] {
    const keys: string[] = ['generalDate'];
    for (const row of rows) {
        if ((row.kind === 'main' || row.kind === 'sub') && row.num) {
            keys.push(`${row.id}.date`);
        }
    }
    return keys;
}

function isBlank(v?: string | null): boolean {
    return v == null || String(v).trim() === '';
}

/** Header keys stored in each page's fieldData (editable overrides API header). */
export const PAGE_HEADER_KEYS = ['client', 'yearEnd', 'fileNo', 'reviewedBy', 'ref'] as const;

export function mergePageHeaderFromApi(
    saved: Record<string, string>,
    header: AuditProgramSectionVO,
    opts: {staffKey: 'completedBy' | 'preparedBy'; defaultRef: string},
): Record<string, string> {
    const staffDefault = header.preparedInitial || header.preparedBy || '';
    const reviewedDefault = header.reviewedInitial || header.reviewedBy || '';
    const fromApi: Record<string, string> = {
        client: header.client ?? '',
        yearEnd: header.yearEnd ?? '',
        fileNo: header.fileNo ?? '',
        [opts.staffKey]: staffDefault,
        reviewedBy: reviewedDefault,
        ref: opts.defaultRef,
    };
    const result = {...fromApi};
    for (const [k, v] of Object.entries(saved)) {
        if (!isBlank(v)) {
            result[k] = v;
        }
    }
    return result;
}

/** Blank A1 fields filled with sample / PCL defaults (Completed → NO). */
export const A1_DEFAULT_VALUES: Record<string, string> = {
    a12Completed: 'NO',
    a13Completed: 'NO',
};

export function mergeA1HeaderFromApi(
    saved: Record<string, string>,
    header: AuditProgramSectionVO,
): Record<string, string> {
    const fromApi: Record<string, string> = {
        client: header.client ?? '',
        yearEnd: header.yearEnd ?? '',
        fileNo: header.fileNo ?? '',
        ref: 'A1',
    };
    const result = {...A1_DEFAULT_VALUES, ...fromApi};
    for (const [k, v] of Object.entries(saved)) {
        if (!isBlank(v)) {
            result[k] = v;
        }
    }
    return result;
}

/** Fill missing keys from defaults; non-blank saved/user values win. */
export function withA12Defaults(
    saved: Record<string, string>,
    opts?: {
        header?: AuditProgramSectionVO;
        enquiryDirectorName?: string | null;
        /** A1 J15 partner date — fills A1.2 I1 / I2 / H11 when those cells are empty */
        a1PartnerDate?: string | null;
    },
): Record<string, string> {
    const defaults: Record<string, string> = {...A12_DEFAULT_VALUES};
    const enquiry = buildA12EnquiryComment(opts?.enquiryDirectorName);
    if (enquiry !== 'Enquired with') {
        defaults['r10.comments'] = enquiry;
    }
    const partnerDate = (opts?.a1PartnerDate || '').trim();
    if (partnerDate) {
        for (const key of A12_DATE_FROM_A1_KEYS) {
            defaults[key] = partnerDate;
        }
    }
    // Sign-off every procedure row with auditor initials (e.g. Eric K.H. Tang → ET).
    const signOff = (opts?.header?.reviewedInitial || opts?.header?.reviewedBy || '').trim();
    if (signOff) {
        for (const key of a12SignOffFieldKeys()) {
            defaults[key] = signOff;
        }
    }
    // Per-row Date column defaults from A1 partner date when present.
    if (partnerDate) {
        for (const key of a12SignOffDateFieldKeys()) {
            defaults[key] = partnerDate;
        }
    }
    const result: Record<string, string> = {...defaults};
    for (const [k, v] of Object.entries(saved)) {
        if (!isBlank(v)) {
            result[k] = v;
        }
    }
    if (opts?.header) {
        return mergePageHeaderFromApi(result, opts.header, {staffKey: 'completedBy', defaultRef: 'A1.2'});
    }
    return result;
}

/**
 * When A1 partnerDate (J15) changes, push into A1.2 header dates + per-row Date column
 * if target is empty or still equals the previous partner date.
 */
export function syncA12DatesFromA1PartnerDate(
    a12: Record<string, string>,
    nextPartnerDate: string,
    prevPartnerDate?: string,
): Record<string, string> {
    const keys = [...A12_DATE_FROM_A1_KEYS, ...a12SignOffDateFieldKeys()];
    return syncDatesFromA1PartnerDate(a12, keys, nextPartnerDate, prevPartnerDate);
}

/** A1.3 H1 preparedDate, H2 reviewedDate — same source as A1 J15 */
export const A13_DATE_FROM_A1_KEYS = ['preparedDate', 'reviewedDate', 'auditReportSignedOn'] as const;

/**
 * Excel A1.3 Comments defaults (H6–H22).
 * Item 4 / 11 = N/A; item 7 = No; others Yes.
 */
export const A13_DEFAULT_VALUES: Record<string, string> = {
    'r6.comments': 'Yes',
    'r7.comments': 'Yes',
    'r8.comments': 'Yes',
    'r9.comments': 'N/A',
    'r10.comments': 'Yes',
    'r11.comments': 'Yes',
    'r12.comments': 'No',
    'r13.comments': 'Yes',
    'r14.comments': 'Yes',
    'r15.comments': 'Yes',
    'r16.comments': 'N/A',
    'r17.comments': 'Yes',
    'r18.comments': 'Yes',
    'r19.comments': 'Yes',
    'r20.comments': 'Yes',
    'r21.comments': 'Yes',
    'r22.comments': 'Yes',
};

/** Parse dd/MM/yyyy or yyyy-MM-dd; return dd/MM/yyyy after adding days, or ''. */
export function addDaysToDateStr(dateStr: string, days: number): string {
    if (!dateStr) return '';
    const s = dateStr.trim();
    let d: Date | null = null;
    const slash = s.split(/[/-]/);
    if (slash.length === 3) {
        if (slash[0].length === 4) {
            d = new Date(Number(slash[0]), Number(slash[1]) - 1, Number(slash[2]));
        } else {
            d = new Date(Number(slash[2]), Number(slash[1]) - 1, Number(slash[0]));
        }
    }
    if (!d || Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Random offset 1–10 for D25 = D24 − N days */
export function randomAssemblyOffsetDays(): number {
    return Math.floor(Math.random() * 10) + 1;
}

/** True if assembly equals deadline − 1..10 days (auto-derived). */
export function isAssemblyDerivedFromDeadline(assembly: string, deadline: string): boolean {
    const a = (assembly || '').trim();
    const d = (deadline || '').trim();
    if (!a || !d) return false;
    for (let i = 1; i <= 10; i++) {
        if (addDaysToDateStr(d, -i) === a) return true;
    }
    return false;
}

/** D25 = D24 − random(1..10) days */
export function buildAssemblyDateFromDeadline(deadline: string, offsetDays?: number): string {
    const offset = offsetDays ?? randomAssemblyOffsetDays();
    return addDaysToDateStr(deadline, -offset);
}

export function withA13Defaults(
    saved: Record<string, string>,
    opts?: {
        header?: AuditProgramSectionVO;
        a1PartnerDate?: string | null;
    },
): Record<string, string> {
    const defaults: Record<string, string> = {...A13_DEFAULT_VALUES};
    const partnerDate = (opts?.a1PartnerDate || '').trim();
    if (partnerDate) {
        // D23 / H1 / H2 ← A1 J15
        for (const key of A13_DATE_FROM_A1_KEYS) {
            defaults[key] = partnerDate;
        }
        // D24 = D23 + 60
        const deadline = addDaysToDateStr(partnerDate, 60);
        if (deadline) {
            defaults.deadlineAssembly = deadline;
            // D25 = D24 − random 1–10
            defaults.fileAssemblyCompleted = buildAssemblyDateFromDeadline(deadline);
        }
    }
    const result: Record<string, string> = {...defaults};
    for (const [k, v] of Object.entries(saved)) {
        if (!isBlank(v)) {
            result[k] = v;
        }
    }
    // If signed/deadline present but assembly still blank after merge, fill D25
    const deadline = (result.deadlineAssembly || '').trim();
    if (deadline && isBlank(result.fileAssemblyCompleted)) {
        result.fileAssemblyCompleted = buildAssemblyDateFromDeadline(deadline);
    }
    if (opts?.header) {
        return mergePageHeaderFromApi(result, opts.header, {staffKey: 'preparedBy', defaultRef: 'A1.3'});
    }
    return result;
}

export function syncA13DatesFromA1PartnerDate(
    a13: Record<string, string>,
    nextPartnerDate: string,
    prevPartnerDate?: string,
): Record<string, string> {
    const next = (nextPartnerDate || '').trim();
    if (!next) return a13;
    const prev = (prevPartnerDate || '').trim();
    const prevDeadline = prev ? addDaysToDateStr(prev, 60) : '';
    let out = syncDatesFromA1PartnerDate(a13, A13_DATE_FROM_A1_KEYS, next, prev);
    const nextDeadline = addDaysToDateStr(next, 60);
    if (nextDeadline) {
        const curDeadline = (out.deadlineAssembly || '').trim();
        if (!curDeadline || curDeadline === prevDeadline) {
            out = {...out, deadlineAssembly: nextDeadline};
        }
        const curAssembly = (out.fileAssemblyCompleted || '').trim();
        const deadlineNow = (out.deadlineAssembly || '').trim();
        if (
            deadlineNow
            && (!curAssembly || (prevDeadline && isAssemblyDerivedFromDeadline(curAssembly, prevDeadline)))
        ) {
            out = {...out, fileAssemblyCompleted: buildAssemblyDateFromDeadline(deadlineNow)};
        }
    }
    return out;
}

function syncDatesFromA1PartnerDate(
    page: Record<string, string>,
    keys: readonly string[],
    nextPartnerDate: string,
    prevPartnerDate?: string,
): Record<string, string> {
    const next = (nextPartnerDate || '').trim();
    if (!next) return page;
    const prev = (prevPartnerDate || '').trim();
    const out = {...page};
    for (const key of keys) {
        const cur = (out[key] || '').trim();
        if (!cur || cur === prev) {
            out[key] = next;
        }
    }
    return out;
}
